import { useTranslation } from "react-i18next";
import { UserPlus } from "lucide-react";
import { Button, cn } from "@tellescope/ui";
import { formatPhone } from "@tellescope/utils";
import type { Participant } from "../useCallWindow";
import { CallHeader } from "../components/CallHeader";
import { ParticipantAvatars } from "../components/ParticipantAvatars";
import { CallControls } from "../components/CallControls";
import { DTMFDialpad } from "../components/DTMFDialpad";
import { ActiveCallMeta, CallDirection, CallStatus, ConferenceState } from "../../../store";

export interface ActiveCallViewProps {
  /** Call direction */
  direction: CallDirection | null;
  /** Current call status */
  status: CallStatus;
  /** Status label to display */
  statusLabel: string;
  /** Call start time for timer */
  startTime: Date | null;
  /** Call metadata */
  activeCallMeta: ActiveCallMeta;
  /** Whether the call is muted */
  isMuted: boolean;
  /** Whether the call is connected */
  isConnected: boolean;
  /** Whether this is a conference call */
  isConference: boolean;
  /** Conference state (if applicable) */
  conference: ConferenceState | null;
  /** List of participants */
  participants: Participant[];
  /** Callback when mute toggle is clicked */
  onToggleMute: () => void;
  /** Callback when hang up is clicked */
  onHangUp: () => void;
  /** Callback when add attendees is clicked */
  onOpenAddAttendees: () => void;
  /** Callback when DTMF digits are sent */
  onSendDigits: (digits: string) => void;
  /** Additional className for the container */
  className?: string;
}

/**
 * ActiveCallView - Main view for an active call.
 *
 * Displays call header, participants with add attendees option,
 * digit input field, and call controls.
 *
 * @example
 * ```tsx
 * <ActiveCallView
 *   direction="outbound"
 *   status="open"
 *   statusLabel="Connected"
 *   startTime={callStartTime}
 *   activeCallMeta={activeCallMeta}
 *   isMuted={isMuted}
 *   isConnected={true}
 *   isConference={false}
 *   conference={null}
 *   participants={participants}
 *   onToggleMute={handleToggleMute}
 *   onHangUp={handleHangUp}
 *   onOpenAddAttendees={() => setShowAddAttendees(true)}
 *   onSendDigits={handleSendDigits}
 * />
 * ```
 */
export function ActiveCallView({
  direction,
  status,
  statusLabel,
  startTime,
  activeCallMeta,
  isMuted,
  isConnected,
  isConference,
  participants,
  onToggleMute,
  onHangUp,
  onOpenAddAttendees,
  onSendDigits,
  className,
}: ActiveCallViewProps) {
  const { t } = useTranslation("calling");

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Header with status and timer */}
      <CallHeader
        direction={direction}
        status={status}
        statusLabel={statusLabel}
        startTime={startTime}
        orgNumber={activeCallMeta.orgNumber}
      />

      {/* Participant section - avatar + name/phone on left, add attendees on right */}
      {/* Add attendees only available for conference calls or inbound calls (direct outbound calls can't be upgraded) */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <ParticipantAvatars participants={participants} maxVisible={4} size="md" />
          {participants.length === 1 && participants[0] != null && (
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{participants[0].name}</div>
              {participants[0].phone && participants[0].name !== participants[0].phone && (
                <div className="text-sm text-muted-foreground truncate">{formatPhone(participants[0].phone)}</div>
              )}
            </div>
          )}
        </div>
        {(isConference || direction === "inbound") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenAddAttendees}
            disabled={!isConnected}
            className="text-muted-foreground hover:text-foreground gap-1.5 shrink-0"
          >
            <UserPlus className="h-4 w-4" />
            {t("Add attendees")}
          </Button>
        )}
      </div>

      {/* DTMF Digit Input */}
      <DTMFDialpad onSendDigits={onSendDigits} disabled={!isConnected} />

      {/* Call Controls (mute + end call) */}
      <CallControls isMuted={isMuted} isConnected={isConnected} onToggleMute={onToggleMute} onHangUp={onHangUp} />
    </div>
  );
}
